import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import './high-level-dashboard.css';
import './high-level-dashboard-dark.css';
import { BuildingUtilization } from './components/building-utilization';
import { HighLevelOverviewExams } from './components/high-level-overview-exams';
import { HighLevelOverviewGeneralBranchInformation } from './components/high-level-overview-general-branch-information';
import { HighLevelReportFinancialMeasures } from './components/financial-measures/high-level-report-financial-measures';
import { HighLevelOverviewBranchesFinancialDetails } from './components/branches-financial-details/high-level-overview-branches-financial-details';
import { getHighOverviewReport } from './operations/get-high-overview-report';
import { HighOverviewReportTypeRes } from '../../../api/types/reports/high-overview/high-overview.type';
import { FilterDialog } from './components/filters/filter-dialog';
import { BuildingUtilizationFilter } from './components/filters/building-utilization-filter';
import { AcademicYearFilter } from './components/filters/academic-year-filter';

export const HighLevelDashboard = () => {
  // Add state for theme
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Toggle theme function
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  };

  // Financial measures filter state
  const [financialMeasuresFilter, setFinancialMeasuresFilter] = useState({
    dateRangeStart: new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1,
    ),
    dateRangeEnd: new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      0,
    ),
  });
  const [showFinancialFilterDialog, setShowFinancialFilterDialog] =
    useState(false);

  // Exams filter state
  const [examsFilter, setExamsFilter] = useState({
    dateRangeStart: new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1,
    ),
    dateRangeEnd: new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      0,
    ),
  });
  const [showExamsFilterDialog, setShowExamsFilterDialog] = useState(false);

  // Building utilization filter state
  const [buildingUtilizationFilter, setBuildingUtilizationFilter] = useState({
    dateRangeStart: new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1,
    ),
    dateRangeEnd: new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      0,
    ),
    timeRangeStart: '08:00',
    timeRangeEnd: '20:00',
  });
  const [
    showBuildingUtilizationFilterDialog,
    setShowBuildingUtilizationFilterDialog,
  ] = useState(false);

  // Academic year filters state (both filters update the same academicYear)
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear());
  const [showExpensesAcademicYearDialog, setShowExpensesAcademicYearDialog] =
    useState(false);
  const [showIncomeAcademicYearDialog, setShowIncomeAcademicYearDialog] =
    useState(false);

  const [data, setData] = useState<HighOverviewReportTypeRes>();
  const getReportData = async () => {
    await getHighOverviewReport(
      setData,
      financialMeasuresFilter,
      examsFilter,
      buildingUtilizationFilter,
      academicYear,
    );
  };
  useEffect(() => {
    getReportData();
  }, [
    financialMeasuresFilter,
    examsFilter,
    buildingUtilizationFilter,
    academicYear,
  ]);

  // Apply financial measures filter
  const handleFinancialFilterApply = (startDate: Date, endDate: Date) => {
    setFinancialMeasuresFilter({
      dateRangeStart: startDate,
      dateRangeEnd: endDate,
    });
    // The useEffect above will automatically trigger when financialMeasuresFilter changes
  };

  // Apply exams filter
  const handleExamsFilterApply = (startDate: Date, endDate: Date) => {
    setExamsFilter({
      dateRangeStart: startDate,
      dateRangeEnd: endDate,
    });
    // The useEffect above will automatically trigger when examsFilter changes
  };

  // Apply building utilization filter
  const handleBuildingUtilizationFilterApply = (
    startDate: Date,
    endDate: Date,
    startTime: string,
    endTime: string,
  ) => {
    setBuildingUtilizationFilter({
      dateRangeStart: startDate,
      dateRangeEnd: endDate,
      timeRangeStart: startTime,
      timeRangeEnd: endTime,
    });
    // The useEffect above will automatically trigger when buildingUtilizationFilter changes
  };

  // Apply academic year filter (shared by both expenses and income)
  const handleAcademicYearFilterApply = (year: number) => {
    setAcademicYear(year);
    // The useEffect above will automatically trigger when academicYear changes
  };

  return (
    <div className="high-level-dashboard">
      <div className="dashboard-header">
        <h1>לוח בקרה ניהולי</h1>
        {/*<Button
          icon={isDarkMode ? 'pi pi-sun' : 'pi pi-moon'}
          onClick={toggleTheme}
          className="theme-toggle-btn"
          aria-label={
            isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
          }
        />*/}
      </div>

      {/* Financial Metrics Section */}
      <div className="financial-metrics-container">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h2 style={{ margin: 0 }}>מדדים פיננסיים</h2>
          <Button
            icon="pi pi-filter"
            label="סינון"
            size="small"
            outlined
            onClick={() => setShowFinancialFilterDialog(true)}
            style={{ fontSize: '12px', padding: '6px 12px' }}
          />
        </div>
        <HighLevelReportFinancialMeasures
          onDataRefresh={getReportData}
          data={data}
        />
      </div>

      {/* Financial Charts Row */}
      <HighLevelOverviewBranchesFinancialDetails
        data={data}
        onExpensesFilterClick={() => setShowExpensesAcademicYearDialog(true)}
        onIncomeFilterClick={() => setShowIncomeAcademicYearDialog(true)}
      />

      {/* Building Utilization and Exams Row */}
      <div className="utilization-exams-row">
        {/* Building Utilization Section */}
        <div className="building-utilization-section">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              /*marginBottom: '16px',*/
              marginTop: '16px',
            }}
          >
            <h2 style={{ margin: 0 }}>אחוז ניצול נכסי נדל״ן החודש</h2>
            <Button
              icon="pi pi-filter"
              label="סינון"
              size="small"
              outlined
              onClick={() => setShowBuildingUtilizationFilterDialog(true)}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            />
          </div>
          <BuildingUtilization data={data} title="" />
        </div>

        {/* Exams Table Section */}
        <div className="exams-section">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '16px',
            }}
          >
            <h2 style={{ margin: 0 }}>מבחני החודש</h2>
            <Button
              icon="pi pi-filter"
              label="סינון"
              size="small"
              outlined
              onClick={() => setShowExamsFilterDialog(true)}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            />
          </div>
          <HighLevelOverviewExams data={data} />
        </div>
      </div>

      {/* Branch Metrics Table Section */}
      <div className="branch-metrics-section">
        <h2>מדדים לפי שלוחה</h2>
        <HighLevelOverviewGeneralBranchInformation data={data} />
      </div>
      {/* Financial Measures Filter Dialog */}

      <FilterDialog
        title={'סינון מדדים פיננסיים'}
        rangeDates={true}
        visible={showFinancialFilterDialog}
        onHide={() => setShowFinancialFilterDialog(false)}
        startDateDefaultValue={financialMeasuresFilter.dateRangeStart}
        endDateDefaultValue={financialMeasuresFilter.dateRangeEnd}
        onFinish={handleFinancialFilterApply}
      />

      {/* Exams Filter Dialog */}
      <FilterDialog
        title={'סינון מבחני החודש'}
        rangeDates={true}
        visible={showExamsFilterDialog}
        onHide={() => setShowExamsFilterDialog(false)}
        startDateDefaultValue={examsFilter.dateRangeStart}
        endDateDefaultValue={examsFilter.dateRangeEnd}
        onFinish={handleExamsFilterApply}
      />

      {/* Building Utilization Filter Dialog */}
      <BuildingUtilizationFilter
        title={'סינון אחוז ניצול נכסי נדל״ן'}
        visible={showBuildingUtilizationFilterDialog}
        onHide={() => setShowBuildingUtilizationFilterDialog(false)}
        startDateDefaultValue={buildingUtilizationFilter.dateRangeStart}
        endDateDefaultValue={buildingUtilizationFilter.dateRangeEnd}
        startTimeDefaultValue={buildingUtilizationFilter.timeRangeStart}
        endTimeDefaultValue={buildingUtilizationFilter.timeRangeEnd}
        onFinish={handleBuildingUtilizationFilterApply}
      />

      {/* Expenses Academic Year Filter Dialog */}
      <AcademicYearFilter
        title={'סינון שנה אקדמית - הוצאות הוראה'}
        visible={showExpensesAcademicYearDialog}
        onHide={() => setShowExpensesAcademicYearDialog(false)}
        academicYearDefaultValue={academicYear}
        onFinish={handleAcademicYearFilterApply}
      />

      {/* Income Academic Year Filter Dialog */}
      <AcademicYearFilter
        title={'סינון שנה אקדמית - הכנסות מסטודנטים'}
        visible={showIncomeAcademicYearDialog}
        onHide={() => setShowIncomeAcademicYearDialog(false)}
        academicYearDefaultValue={academicYear}
        onFinish={handleAcademicYearFilterApply}
      />
    </div>
  );
};
